import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {
    Drawer,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    DrawerOverlay,
    DrawerContent,
    Image,
    useDisclosure,
    useBreakpointValue,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    useAccessToken,
    useCommerceApi,
    useProducts,
    useShopperBasketsMutation,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useToast} from '@salesforce/retail-react-app/app/hooks/use-toast'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {CookieStorage} from '@salesforce/commerce-sdk-react/auth/storage'
import {onClient} from '@salesforce/commerce-sdk-react/utils'
import {usePromotions} from '@salesforce/commerce-sdk-react'
import {useGlobaleContext} from '../../../contexts/globale'
import Header from './header'
import Body from './body'
import Footer from './footer'
import useTagManager from '../../../hooks/use-tag-manager'
import CustomApi from '../../../hooks/use-custom-api'
import LoadingSpinner from '../../loading-spinner'
import {getUrl} from '../../../utils/cloudinary-utils'
import {useSiteContext} from '../../../contexts/site'
import {
    getBasketProductIds,
    getGwpMeterData,
    getQualifyingProductIds
} from '../../../utils/basket-utils'
import {yieldToMain} from '../../../utils/common-utils'
import {
    GWP_PROMOTION_COOKIE_ID,
    GWP_PROMOTION_ID_REGEX,
    GWP_THRESHOLD_COOKIE_KEY,
    SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
} from '../../../constants'
import {
    clearBonusModalTargetPromotionId,
    getBonusModalTargetPromotionId
} from '../../../utils/bonus-modal-target'
import {getSource} from '../../../utils/product-utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import {logBasketErrorToSsr} from '../../../utils/log-basket-error-to-ssr'

const COOKIE_ID = GWP_PROMOTION_COOKIE_ID

const GWPModal = () => {
    const selectedAttributes = SELECTED_ATTRIBUTES_WIDGET_PRODUCTS
    const {formatMessage} = useIntl()
    const showToast = useToast()
    const {getTokenWhenReady} = useAccessToken()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const customapi = new CustomApi()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const {isOpen, onOpen, onClose} = useDisclosure()
    const [actionSpinner, showActionSpinner] = useState(false)
    const [forceOpen, setForceOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [showSpinner, setShowSpinner] = useState(false)
    const tagManager = useTagManager()
    const {
        setOpenBonusModal,
        openBonusModal,
        editBonusItemInCart,
        setEditBonusItemInCart,
        setForceOpenGwpDrawer,
        forceOpenGwpDrawer
    } = useSiteContext()
    const [closedByCustomer, setClosedByCustomer] = useState(false)
    const [selectedBonuses, setSelectedBonuses] = useState([])
    const [showAddToBagError, setShowAddToBagError] = useState(false)
    const [meterType, setMeterType] = useState('')
    const placement = useBreakpointValue({base: 'bottom', lg: 'right'})
    const {countryISO, currencyCode} = useGlobaleContext()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const targetPromoId = getBonusModalTargetPromotionId()
    const preferredPromoId =
        targetPromoId &&
        basket?.bonusDiscountLineItems?.some((i) => i?.promotionId === targetPromoId)
            ? targetPromoId
            : null
    const promoId =
        preferredPromoId ||
        (
            basket?.bonusDiscountLineItems?.find((i) =>
                GWP_PROMOTION_ID_REGEX.test(i.promotionId)
            ) || basket?.bonusDiscountLineItems?.[0]
        )?.promotionId // Default: prioritize GWP if present.
    const {data: promotionsData} = usePromotions(
        {
            parameters: {
                ids: [promoId]
            }
        },
        {
            enabled: Boolean(promoId)
        }
    )

    const promotionDetails = promotionsData?.data?.[0]

    /********* Logic to show the GWP/PWP modal again based on the Threshold *********/
    let meterConfig = ''
    try {
        meterConfig = promotionDetails?.c_bmsmAndGwpMeterConfig
            ? JSON.parse(promotionDetails?.c_bmsmAndGwpMeterConfig)
            : ''
    } catch (e) {
        console.error(e.message)
    }
    const basketProductIds = useMemo(() => getBasketProductIds(basket), [basket?.productItems])
    const {data: qualifyingProductsData, isLoading: isQualifyingProductsLoading} = useProducts(
        {
            parameters: {
                ids: basketProductIds.join(','),
                expand: ['promotions'],
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: Boolean(promoId && basketProductIds.length)
        }
    )

    const flyOutGtmEvent = () => {
        if (promotionDetails) {
            const source = getSource()
            window.__dataLayerLoaded = true
            tagManager.promoMessageImpression({
                id: promotionDetails.id,
                name: promotionDetails.c_promotionHeader,
                creative:
                    (GWP_PROMOTION_ID_REGEX?.test(promotionDetails?.id) ? 'GWP' : 'PWP') +
                    ` Banner - Flyout Modal`,
                position: source
            })
            window.__dataLayerLoaded = false
        }
    }

    const qualifyingProductIds = useMemo(
        () =>
            isQualifyingProductsLoading
                ? undefined
                : getQualifyingProductIds(qualifyingProductsData?.data, promoId),
        [qualifyingProductsData?.data, promoId, isQualifyingProductsLoading]
    )
    const activeLevel = getGwpMeterData(basket, meterConfig, promoId, qualifyingProductIds)
    useEffect(() => {
        if (isQualifyingProductsLoading || !promoId) {
            return
        }
        if (
            `${activeLevel?.reached}-${activeLevel?.threshold}-${promoId}` !==
                cookie.get(GWP_THRESHOLD_COOKIE_KEY) &&
            !!cookie.get(GWP_THRESHOLD_COOKIE_KEY) &&
            activeLevel?.reached
        ) {
            /* 
                We'll force the modal to open if:
                1. Current threshold is not equal to the previous one
                2. Current threshold is reached
                3. Previous threshold was set before
            */
            onOpen()
            setForceOpenGwpDrawer(true)
            flyOutGtmEvent()
            cookie.set(
                GWP_THRESHOLD_COOKIE_KEY,
                `${activeLevel?.reached}-${activeLevel?.threshold}-${promoId}`
            )
        } else if (!cookie.get(GWP_THRESHOLD_COOKIE_KEY)) {
            cookie.set(
                GWP_THRESHOLD_COOKIE_KEY,
                `${activeLevel?.reached || '-'}-${activeLevel?.threshold || '-'}-${promoId || '-'}`
            )
        }
    }, [activeLevel?.reached, activeLevel?.threshold, isQualifyingProductsLoading, promoId])
    /********* Logic to show the GWP/PWP modal again based on the Threshold *********/

    const cookie =
        onClient() &&
        new CookieStorage({
            keySuffix: '',
            sharedContext: !onClient()
        })
    const gwpPromotionState = onClient() && cookie ? cookie.get(COOKIE_ID) : null

    const bonusDiscountLineItems = basket?.bonusDiscountLineItems?.filter(
        (item) => item.promotionId === promoId
    )?.[0]

    const bonusItemsInBasket =
        basket?.productItems
            ?.filter(
                (item) =>
                    item.bonusDiscountLineItemId === bonusDiscountLineItems?.id &&
                    !selectedBonuses.find(
                        (selectedBonus) => selectedBonus.variant.productId === item.productId
                    )
            )
            ?.reduce((acc, item) => acc + item.quantity, 0) || 0
    const totalSelectedBonuses =
        selectedBonuses.reduce((acc, bonus) => acc + (bonus.quantity || 1), 0) + bonusItemsInBasket
    const bonusItemsInBasketArray = basket?.productItems?.filter(
        (item) => item.bonusDiscountLineItemId === bonusDiscountLineItems?.id
    )
    const [globalePrices, setGlobalePrices] = useState(null)
    const productIds = bonusDiscountLineItems?.bonusProducts?.map((item) => item.productId) || []
    const enabledProductsFetch =
        Boolean(productIds.length) &&
        (!closedByCustomer || forceOpenGwpDrawer) &&
        (totalSelectedBonuses < bonusDiscountLineItems?.maxBonusItems || editBonusItemInCart) &&
        (!gwpPromotionState || forceOpenGwpDrawer)
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds.join(','),
                expand: ['availability', 'links', 'promotions', 'images', 'prices', 'variations'],
                perPricebook: true,
                select: selectedAttributes,
                allVariationProperties: true,
                allImages: true,
                c_selectedCountry: countryISO,
                c_selectedCurrency: currencyCode
            }
        },
        {
            enabled: enabledProductsFetch
        }
    )
    const bonuses =
        products?.data
            ?.map((item) => ({
                ...item,
                ...(item.variants
                    ? {
                          variants: item.variants?.map((variant) => ({
                              ...variant,
                              price: 0
                          }))
                      }
                    : {}),
                variationValues:
                    item?.variationValues ||
                    (item?.variants?.length === 1 && item?.variants?.[0]?.variationValues) ||
                    {},
                price: 0
            }))
            ?.sort((a, b) => {
                const aOrderable = !!a?.inventory?.orderable
                const bOrderable = !!b?.inventory?.orderable
                if (aOrderable === bOrderable) return 0
                return aOrderable ? -1 : 1
            }) || []

    const showError = () => {
        showToast({
            title: formatMessage(API_ERROR_MESSAGE),
            status: 'error'
        })
    }

    useEffect(() => {
        const getGlobalePrices = async () => {
            setShowSpinner(true)
            const token = await getTokenWhenReady()
            const parameters = {
                c_currencyCode: currencyCode,
                c_countryISO: countryISO
            }
            const response = await customapi.post(token, 'globale-prices', '/', parameters, proxy)
            setShowSpinner(false)
            if (response?.bonusPrices) {
                setGlobalePrices(response?.bonusPrices)
            }
        }
        if (isOpen && !isProductsLoading) {
            getGlobalePrices()
        }
    }, [isOpen, bonuses?.length, isProductsLoading])

    const handleClose = async () => {
        showActionSpinner(true)
        await yieldToMain()
        setClosedByCustomer(true)
        setForceOpenGwpDrawer(false)
        if (cookie) {
            cookie.set(COOKIE_ID, 'closed')
        }
        setOpenBonusModal(false)
        setEditBonusItemInCart(false)
        clearBonusModalTargetPromotionId()
        onClose()
        setForceOpen(false)
        showActionSpinner(false)
        setSelectedBonuses([])
    }

    useEffect(() => {
        if (!basket?.productItems?.length && !isBasketLoading && !bonuses?.length) {
            cookie.delete(COOKIE_ID)
            setOpenBonusModal(false)
            setEditBonusItemInCart(false)
            clearBonusModalTargetPromotionId()
        }
    }, [basket?.productItems?.length, isBasketLoading])

    useEffect(() => {
        if (promotionDetails?.id?.toLowerCase()?.indexOf('gwp') !== -1) {
            setMeterType('GWP')
        } else if (promotionDetails?.id?.toLowerCase()?.indexOf('pwp') !== -1) {
            setMeterType('PWP')
        } else {
            setMeterType('BMSM')
        }
    }, [promotionDetails?.id])

    // update the quantity of bonus item(s) if we have any in the basket
    const updateItemsInBasket = async (body) => {
        await updateItemsInBasketMutation.mutateAsync({
            method: 'PATCH',
            parameters: {
                basketId: basket.basketId
            },
            body: body
        })
        return true
    }

    const handleAddToBag = async () => {
        if (selectedBonuses.length === 0) {
            setShowAddToBagError(true)
            return
        }

        let productItems = []

        try {
            setIsLoading(true)
            let toBeAdded = selectedBonuses.map(({variant, quantity}) => ({
                productId: variant.productId,
                price: variant.price,
                quantity,
                bonusDiscountLineItemId: bonusDiscountLineItems?.id
            }))
            let updatedBonusItems = []
            toBeAdded = toBeAdded.filter(function (itemToAdd) {
                var existingItem = basket?.productItems?.find(function (p) {
                    return (
                        p.productId === itemToAdd.productId &&
                        p.bonusProductLineItem === true &&
                        p.bonusDiscountLineItemId === itemToAdd.bonusDiscountLineItemId
                    )
                })

                if (existingItem) {
                    updatedBonusItems.push({
                        productId: existingItem.productId,
                        itemId: existingItem.itemId,
                        price: itemToAdd.price,
                        quantity: existingItem.quantity + itemToAdd.quantity,
                        bonusDiscountLineItemId: existingItem.bonusDiscountLineItemId
                    })

                    return false
                }

                return true
            })
            const newItemsAdded = toBeAdded?.length
                ? await addItemToNewOrExistingBasket(toBeAdded)
                : null
            /**
             * NOTE:
             * We need to use 'updateItemsInBasketMutation' rather than 'addItemToNewOrExistingBasket',
             * because 'addItemToNewOrExistingBasket' will result in addition of new bonus line item.
             * **/
            const itemsUpdated = updatedBonusItems?.length
                ? await updateItemsInBasket(updatedBonusItems)
                : null
            if (newItemsAdded || itemsUpdated) {
                tagManager.addToCart(
                    products?.data,
                    null,
                    [...toBeAdded, ...updatedBonusItems],
                    null,
                    null,
                    meterType
                )

                const itemsAdded = selectedBonuses.map(({variant, quantity}) => {
                    const matchedProduct = bonuses.find(
                        (b) =>
                            b.id === variant.productId ||
                            b.variants?.some((v) => v.productId === variant.productId)
                    )
                    return {
                        product: {
                            ...matchedProduct,
                            ...variant,
                            quantity
                        }
                    }
                })

                tagManager.addToCartGA4({
                    itemsAdded,
                    method: meterType,
                    currency: currencyCode,
                    listName: meterType,
                    listId: meterType
                })

                let type /** page type **/
                if (document?.querySelector('.sf-product-list-page')) {
                    type = 'plp'
                } else if (document?.querySelector('.sf-product-detail-page')) {
                    type = 'pdp'
                } else {
                    type = 'home'
                }
                const totalQty = selectedBonuses?.reduce((sum, b) => sum + b.quantity, 0)
                const promo = {
                    id: promotionDetails.id,
                    name: promotionDetails.name,
                    creative: promotionDetails.details,
                    position: type,
                    quantity: totalQty
                }
                tagManager.promotionClick(promo, meterType)
            }

            if (document.querySelector('.bonus-stack')) {
                document.querySelector('.bonus-stack').style.filter = 'blur(4px)'
            }
            setIsLoading(false)
            handleClose()
        } catch (error) {
            const errResponse = error?.response ? await error.response.json() : null
            console.log(error.message)
            logger.error('ATCXL Error adding bonus product to basket', {
                namespace: 'GwpModal.handleAddToBag',
                additionalProperties: {error, errResponse, basketId: basket?.basketId, productItems}
            })
            logBasketErrorToSsr('GwpModal.handleAddToBag', {
                error,
                errResponse,
                basketId: basket?.basketId,
                productItems
            })
            showError(error)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        if (!isOpen && bonuses.length > 0 && !closedByCustomer) {
            onOpen()
            flyOutGtmEvent()
        }
    }, [bonuses])

    const drawerShouldRender =
        isOpen &&
        bonuses?.length > 0 &&
        (gwpPromotionState !== 'closed' || forceOpen) &&
        openBonusModal &&
        (bonusItemsInBasket < bonusDiscountLineItems?.maxBonusItems || editBonusItemInCart)

    useEffect(() => {
        if (drawerShouldRender) {
            document.querySelector('html').style.overflow = 'hidden'
        } else {
            document.querySelector('html').style.overflow = ''
        }
    }, [drawerShouldRender])

    return (
        <Box>
            {actionSpinner && <LoadingSpinner position="fixed" top="auto" />}
            {(drawerShouldRender || forceOpenGwpDrawer) && bonuses?.length ? (
                <Drawer
                    isOpen
                    blockScrollOnMount
                    onClose={handleClose}
                    placement={placement}
                    className="gwp-drawer"
                >
                    <DrawerOverlay />
                    <DrawerContent
                        className="gwp-drawer-content"
                        data-meter-type={meterType}
                        maxWidth={{base: '100%', lg: '443px'}}
                        height={{
                            base:
                                bonuses?.length === 1
                                    ? promotionDetails?.c_showProductDescription
                                        ? '80%'
                                        : '68%'
                                    : '90%',
                            lg: '100%'
                        }}
                        top={{base: 'auto !important', lg: '0'}}
                        borderRadius={{base: '20px 20px 0 0', lg: 0}}
                    >
                        <Box
                            position="absolute"
                            top="10px"
                            right="10px"
                            _hover={{
                                backgroundColor: 'gray.100'
                            }}
                            cursor="pointer"
                            borderRadius="50%"
                            padding="15px"
                            transition="all, 0.3s ease"
                            onClick={handleClose}
                        >
                            <Image
                                width="15px"
                                height="15px"
                                alt="Cross Icon"
                                src={getUrl('UIImages/images/crossed-icon.svg')}
                            />
                        </Box>
                        <DrawerHeader
                            paddingX={{base: '10px', xs: '20px', md: '40px'}}
                            paddingTop={{base: '33px', xs: '40px', lg: '50px'}}
                            paddingBottom={{base: '20px', xs: '27px', md: '40px'}}
                            boxShadow="none"
                            className="gwp-drawer-header"
                        >
                            {promotionDetails && (
                                <Header
                                    title={promotionDetails.c_promotionHeader}
                                    isBasketLoading={isBasketLoading}
                                    subTitle={promotionDetails.c_promotionSubHeader}
                                    body={promotionDetails.c_promotionBody}
                                    promotionDetails={promotionDetails}
                                    bonuses={bonuses}
                                    bonusItemsInBasketArray={bonusItemsInBasketArray}
                                    totalSelectedBonuses={
                                        selectedBonuses?.reduce((total, item) => {
                                            return total + (item?.quantity || 0)
                                        }, 0) || 0
                                    }
                                    maxBonusItems={bonusDiscountLineItems?.maxBonusItems}
                                />
                            )}
                        </DrawerHeader>

                        <DrawerBody
                            className="gwp-drawer-body"
                            paddingX={{base: '10px', xs: '20px', md: '40px'}}
                            paddingTop="0 !important"
                            sx={{
                                overflowX: 'auto',
                                '&::-webkit-scrollbar': {
                                    width: {lg: '8px'}
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: {lg: 'gray.800'},
                                    borderRadius: {lg: '2px'},
                                    display: {lg: 'block'}
                                },
                                '&::-webkit-scrollbar-track': {
                                    backgroundColor: {lg: 'gray.400'},
                                    borderRadius: {lg: '2px'},
                                    display: {lg: 'block'}
                                },
                                '.list-price': {
                                    display: promotionDetails?.c_showRetailPrice
                                        ? 'inline-flex'
                                        : 'none'
                                },
                                userSelect: {lg: 'none'},
                                scrollBehavior: {lg: 'smooth'}
                            }}
                        >
                            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}
                            {promotionDetails && (
                                <Body
                                    bonuses={bonuses}
                                    globalePrices={globalePrices}
                                    maxBonusItems={bonusDiscountLineItems?.maxBonusItems}
                                    promoId={promoId}
                                    bonusItemsInBasketArray={bonusItemsInBasketArray}
                                    totalSelectedBonuses={
                                        selectedBonuses?.reduce((total, item) => {
                                            return total + (item?.quantity || 0)
                                        }, 0) || 0
                                    }
                                    selectedBonuses={selectedBonuses}
                                    meterType={meterType}
                                    setSelectedBonuses={setSelectedBonuses}
                                    setShowAddToBagError={setShowAddToBagError}
                                    showProductDescription={
                                        promotionDetails.c_showProductDescription
                                    }
                                    handleClose={handleClose}
                                />
                            )}
                        </DrawerBody>

                        <DrawerFooter
                            className="gwp-drawer-footer"
                            gap={{base: '14px', xs: '20px'}}
                            paddingX={{base: '10px', xs: '20px', md: '40px'}}
                            paddingY={{base: '14px', xs: '20px'}}
                            borderTop="1px solid"
                            borderColor="gray.600"
                            flexWrap="wrap"
                        >
                            <Footer
                                onClose={handleClose}
                                meterType={meterType}
                                handleAddToBag={handleAddToBag}
                                bonuses={bonuses}
                                selectedBonuses={selectedBonuses}
                                isLoading={isLoading}
                                showAddToBagError={showAddToBagError}
                            />
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>
            ) : null}
        </Box>
    )
}

export default GWPModal
